import React from 'react';
import { Alert, Box, Button, Card, CardContent, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import type { Profitability } from './types';

// Resumen del tablero: ventas netas − costo directo − costos indirectos = ganancia, y los productos que más dejan.

const money = (n: number) => `$${new Intl.NumberFormat('es-AR', { maximumFractionDigits: 0 }).format(Math.round(n))}`;
const pct = (n: number) => `${(n * 100).toFixed(1).replace('.', ',')}%`;

const ProfitSummary: React.FC<{ data: Profitability; onOpen: () => void }> = ({ data, onOpen }) => {
  const t = data.totals;
  const top = [...data.products].sort((a, b) => b.netProfit - a.netProfit).slice(0, 5);
  const steps: { label: string; value: number; strong?: boolean }[] = [
    { label: 'Ventas netas (sin IVA)', value: t.revenueNet },
    { label: '− Costo de producción (recetas)', value: -t.productionCost },
    { label: '− Comisiones de los canales', value: -t.sellingCost },
    { label: '= Contribución', value: t.contribution, strong: true },
    { label: '− Gastos fijos y del período', value: -t.indirectCost },
    { label: '= Ganancia neta', value: t.netProfit, strong: true },
  ];
  return (
    <Card sx={{ mb: 4 }}>
      <CardContent>
        <Stack direction="row" alignItems="center" sx={{ mb: 1 }}>
          <Typography variant="h6" sx={{ flex: 1 }}>Resultado del período</Typography>
          <Button size="small" onClick={onOpen}>Ver rentabilidad por producto</Button>
        </Stack>
        {t.costCoverage < 0.95 && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Solo el {pct(t.costCoverage)} de las ventas tiene el costo completo: lo que no tiene costo cuenta como $0 y la ganancia aparece más alta.
          </Alert>
        )}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1.4fr' }, gap: 3 }}>
          <Table size="small">
            <TableBody>
              {steps.map(s => (
                <TableRow key={s.label}>
                  <TableCell sx={{ fontWeight: s.strong ? 700 : 400, borderBottom: s.strong ? undefined : 'none' }}>{s.label}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: s.strong ? 700 : 400, borderBottom: s.strong ? undefined : 'none', color: s.strong ? (s.value < 0 ? 'error.main' : 'success.main') : undefined }}>
                    {money(s.value)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Los productos que más dejan</Typography>
            <Table size="small">
              <TableHead>
                <TableRow><TableCell>Producto</TableCell><TableCell align="right">Unidades</TableCell><TableCell align="right">Contribución</TableCell><TableCell align="right">Ganancia neta</TableCell></TableRow>
              </TableHead>
              <TableBody>
                {top.length === 0 && <TableRow><TableCell colSpan={4} sx={{ color: 'text.secondary' }}>Sin ventas en el período.</TableCell></TableRow>}
                {top.map(p => (
                  <TableRow key={p.productId}>
                    <TableCell>{p.name}{p.costSource === 'sin-costo' || p.costIncomplete ? <Typography component="span" variant="caption" color="warning.main"> · costo incompleto</Typography> : null}</TableCell>
                    <TableCell align="right">{new Intl.NumberFormat('es-AR').format(p.units)}</TableCell>
                    <TableCell align="right">{money(p.contribution)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600, color: p.netProfit < 0 ? 'error.main' : 'success.main' }}>{money(p.netProfit)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export default ProfitSummary;
